import { useEffect, useMemo, useState } from "react";
import "./quizPage.css";

const MAX_NAME_WIDTH = 15;

function isCjkCharacter(character) {
  return /[\u3400-\u4DBF\u4E00-\u9FFF\uF900-\uFAFF]/u.test(character);
}

function getCharacterWidth(character) {
  if (/^[\uFF01-\uFF60\uFFE0-\uFFE6]$/u.test(character)) {
    return 2;
  }

  return isCjkCharacter(character) ? 2 : 1;
}

function limitNameInput(value) {
  const nextCharacters = [];
  let totalWidth = 0;

  for (const character of Array.from(value)) {
    const characterWidth = getCharacterWidth(character);

    if (totalWidth + characterWidth > MAX_NAME_WIDTH) {
      break;
    }

    nextCharacters.push(character);
    totalWidth += characterWidth;
  }

  return nextCharacters.join("");
}

function QuizPage() {
  const goResultPage = (name, questionSetId, score, recordId) => {
    const params = new URLSearchParams();

    if (name) {
      params.set("name", name);
    }

    if (questionSetId) {
      params.set("questionSetId", String(questionSetId));
    }

    if (Number.isFinite(score)) {
      params.set("score", String(score));
    }

    if (recordId) {
      params.set("recordId", String(recordId));
    }

    const queryString = params.toString();
    window.location.assign(queryString ? `/result?${queryString}` : "/result");
  };
  const searchParams = useMemo(
    () => new URLSearchParams(window.location.search),
    [],
  );
  const questionSetName = searchParams.get("questionSetName") || "0-3";

  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [questionSet, setQuestionSet] = useState(null);
  const [groups, setGroups] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [userName, setUserName] = useState("");
  const [submissionError, setSubmissionError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    async function requestLatestQuestionSet() {
      const envApiBaseUrl = process.env.REACT_APP_API_BASE_URL;
      const currentHostApi = `${window.location.protocol}//${window.location.hostname}:8443/api`;
      const candidateBaseUrls = [
        envApiBaseUrl,
        "/api",
        currentHostApi,
        "http://localhost:8443/api",
      ]
        .filter(Boolean)
        .filter((value, index, arr) => arr.indexOf(value) === index);

      let lastError = null;
      const attemptedUrls = [];

      for (const apiBaseUrl of candidateBaseUrls) {
        try {
          const endpointPath = "/super-daddy-tests/latest";
          const requestBase = apiBaseUrl.startsWith("http")
            ? apiBaseUrl
            : `${window.location.origin}${apiBaseUrl}`;
          const url = new URL(`${requestBase}${endpointPath}`);
          url.searchParams.set("questionSetName", questionSetName);
          attemptedUrls.push(url.toString());

          const response = await fetch(url.toString(), {
            signal: controller.signal,
          });

          const contentType = response.headers.get("content-type") || "";
          const rawBody = await response.text();

          if (!contentType.includes("application/json")) {
            throw new Error(
              `API ${url.pathname} 回傳非 JSON（可能打到前端頁面）。`,
            );
          }

          const payload = JSON.parse(rawBody);

          if (!response.ok || !payload.success) {
            throw new Error(payload.message || "Failed to load quiz data");
          }

          return payload;
        } catch (error) {
          if (error.name === "AbortError") {
            throw error;
          }
          lastError = error;
        }
      }

      if (lastError instanceof TypeError) {
        throw new Error(`無法連線到 API。已嘗試：${attemptedUrls.join(" | ")}`);
      }

      throw lastError || new Error("Failed to load quiz data");
    }

    async function loadQuizData() {
      setLoading(true);
      setErrorMessage("");

      try {
        const payload = await requestLatestQuestionSet();

        setQuestionSet(payload.data?.questionSet || null);
        setGroups(
          Array.isArray(payload.data?.groups) ? payload.data.groups : [],
        );
        setCurrentQuestionIndex(0);
        setSelectedAnswers([]);
        setUserName("");
      } catch (error) {
        if (error.name === "AbortError") {
          return;
        }
        setErrorMessage(error.message || "Failed to load quiz data");
      } finally {
        setLoading(false);
      }
    }

    loadQuizData();

    return () => controller.abort();
  }, [questionSetName]);

  const flattenedQuestions = useMemo(() => {
    return groups.flatMap((group) => {
      const questions = Array.isArray(group.questions) ? group.questions : [];
      return questions.map((question) => ({
        ...question,
        groupDimension: group.dimension,
        groupName: group.name,
        // 隨機排序答案
        answers: question.answers
          ? [...question.answers].sort(() => Math.random() - 0.5)
          : [],
      }));
    });
  }, [groups]);

  const currentQuestion = flattenedQuestions[currentQuestionIndex] || null;
  const isFinished =
    !loading &&
    !errorMessage &&
    flattenedQuestions.length > 0 &&
    currentQuestionIndex >= flattenedQuestions.length;

  function handleSelectAnswer(answer) {
    if (!currentQuestion) {
      return;
    }

    setSelectedAnswers((prev) => [
      ...prev,
      {
        questionId: currentQuestion.id,
        answerId: answer.id,
        score: answer.score,
      },
    ]);
    setCurrentQuestionIndex((prev) => prev + 1);
  }

  async function handleFinishQuiz() {
    const trimmedName = userName.trim();

    if (!trimmedName) {
      setSubmissionError("請先輸入你的名字。");
      return;
    }

    if (!questionSet?.id) {
      setSubmissionError("找不到題庫資料，請重新整理後再試。");
      return;
    }

    if (selectedAnswers.length === 0) {
      setSubmissionError("未有答案可提交。");
      return;
    }

    setSubmitting(true);
    setSubmissionError("");

    try {
      const questionById = flattenedQuestions.reduce(
        (accumulator, question) => {
          accumulator[question.id] = question;
          return accumulator;
        },
        {},
      );

      const dimensionScores = selectedAnswers.reduce((accumulator, answer) => {
        const question = questionById[answer.questionId];
        const dimension = question?.groupDimension ?? question?.dimension;
        if (!dimension) {
          return accumulator;
        }

        accumulator[dimension] =
          (accumulator[dimension] || 0) + Number(answer.score || 0);
        return accumulator;
      }, {});

      const questionSetScore = selectedAnswers.reduce(
        (sum, answer) => sum + Number(answer.score || 0),
        0,
      );

      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || "/api";
      const requestBase = apiBaseUrl.startsWith("http")
        ? apiBaseUrl
        : `${window.location.origin}${apiBaseUrl}`;
      const url = new URL(`${requestBase}/records/init`);

      const response = await fetch(url.toString(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          questionSetId: questionSet.id,
          datetime: new Date().toISOString(),
          name: trimmedName,
          dimension1Score: dimensionScores[1] || null,
          dimension2Score: dimensionScores[2] || null,
          dimension3Score: dimensionScores[3] || null,
          dimension4Score: dimensionScores[4] || null,
          dimension5Score: dimensionScores[5] || null,
          questionSetScore,
          answers: selectedAnswers.map((answer) => ({
            questionId: answer.questionId,
            answerId: answer.answerId,
          })),
        }),
      });

      const contentType = response.headers.get("content-type") || "";
      const responseBody = contentType.includes("application/json")
        ? await response.json()
        : { success: false, message: await response.text() };

      if (!response.ok || !responseBody.success) {
        throw new Error(responseBody.message || "Failed to submit quiz result");
      }

      const normalizedDimensionScores = {
        1: dimensionScores[1] || 0,
        2: dimensionScores[2] || 0,
        3: dimensionScores[3] || 0,
        4: dimensionScores[4] || 0,
        5: dimensionScores[5] || 0,
      };

      window.sessionStorage.setItem(
        "superDaddyResultContext",
        JSON.stringify({
          name: trimmedName,
          questionSetId: questionSet.id,
          questionSetScore,
          dimensionScores: normalizedDimensionScores,
          recordId: responseBody.data?.recordId || null,
        }),
      );

      goResultPage(
        trimmedName,
        questionSet.id,
        questionSetScore,
        responseBody.data?.recordId,
      );
    } catch (error) {
      setSubmissionError(error.message || "Failed to submit quiz result");
    } finally {
      setSubmitting(false);
    }
  }

  const loadingIcon = (
    <div className="quiz-loading-wrap" role="status" aria-live="polite">
      <span className="quiz-loading-icon" aria-hidden="true" />
    </div>
  );

  const progressPercent = useMemo(() => {
    const total = flattenedQuestions.length || 0;
    if (total === 0) return 0;
    const raw = (currentQuestionIndex / total) * 100;
    return Math.max(0, Math.min(100, raw));
  }, [currentQuestionIndex, flattenedQuestions.length]);

  function handleGoBack() {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
      setSelectedAnswers((prev) => prev.slice(0, -1));
    }
  }

  function handleUserNameChange(event) {
    setUserName(limitNameInput(event.target.value));
  }

  return (
    <main className="quiz-page">
      <div className="quiz-page-bg" aria-hidden="true" />

      <section className={`quiz-card${isFinished ? " quiz-card-blurred" : ""}`}>
        <img
          className="rock-bg"
          src="/images/P5_Banner_01.webp"
          alt=""
          aria-hidden="true"
        />

        <div className="quiz-card-content">
          <div className="progress-bar-wrapper">
            <div
              className="progress-bar"
              style={{
                width: `${(currentQuestionIndex / flattenedQuestions.length) * 100}%`,
              }}
            />
            <img
              className="progress-ship"
              src="/images/P5_BarShip_01.webp"
              alt=""
              aria-hidden="true"
              style={{ left: `${progressPercent}%` }}
            />
            <img
              className="progress-flag"
              src="/images/P5_Flag_01.webp"
              alt=""
              aria-hidden="true"
            />
          </div>
          <div className="quiz-title">{questionSetName}歲</div>
          <img
            className="quiz-card-deco"
            src={`/images/questionDeco/${questionSetName}/Q${currentQuestionIndex + 1}.webp`}
            alt=""
            aria-hidden="true"
          />

          {loading ? loadingIcon : null}

          {!loading && errorMessage ? (
            <p className="quiz-question">{errorMessage}</p>
          ) : null}

          {!loading && !errorMessage ? (
            <>
              {flattenedQuestions.length === 0 ? loadingIcon : null}

              {currentQuestion ? (
                <>
                  <p className="quiz-question">{currentQuestion.text}</p>

                  <div className="quiz-ans-container">
                    {(currentQuestion.answers || []).map((answer) => (
                      <button
                        key={answer.id}
                        type="button"
                        className="quiz-ans-btn"
                        onClick={() => handleSelectAnswer(answer)}
                      >
                        {answer.text}
                      </button>
                    ))}
                    <button
                      type="button"
                      className={`quiz-back-btn${currentQuestionIndex === 0 ? " disabled" : ""}`}
                      onClick={
                        currentQuestionIndex > 0 ? handleGoBack : undefined
                      }
                      disabled={currentQuestionIndex === 0}
                    >
                      <img
                        src="/images/P2_Back_Button_01.webp"
                        alt="Go Back"
                        className="quiz-back-btn-icon"
                      />
                    </button>
                  </div>
                </>
              ) : null}
            </>
          ) : null}
        </div>
      </section>

      {isFinished ? (
        <div className="quiz-finish-overlay" role="dialog" aria-modal="true">
          <div className="quiz-finish-popup">
            <p className="quiz-finish-title">輸入你的名字</p>
            <input
              type="text"
              className="quiz-name-input"
              value={userName}
              onChange={handleUserNameChange}
              placeholder="請輸入名字"
            />
            {submissionError ? (
              <p className="quiz-finish-error">{submissionError}</p>
            ) : null}
            <button
              type="button"
              className="quiz-finish-btn"
              onClick={handleFinishQuiz}
              disabled={submitting}
            >
              {submitting ? "提交中..." : "查看你的爸爸風格"}
            </button>
          </div>
        </div>
      ) : null}
    </main>
  );
}

export default QuizPage;
